import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Wine } from 'lucide-react';
import { formatProductDisplay } from '../../utils/formatProduct';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  const t = url.trim();
  return t.startsWith('http') ? t : `${API_URL}${t}`;
};

// Map a proWineType string to a CSS background class.
// Strips diacritics so "Rosé" → "sf-bg--rose", "Sparkling" → "sf-bg--sparkling", etc.
function wineTypeBgClass(type) {
  if (!type) return 'sf-bg--default';
  const normalized = type
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // é→e, è→e, etc.
    .replace(/\s+/g, '-');
  return `sf-bg--${normalized}`;
}

// ── Qty control (also exported for use in ProductDrawer) ─────────────────────
export function QtyControl({ qty, min, onChange }) {
  return (
    <div className="sf-qty-control">
      <button
        className="sf-qty-control__btn"
        type="button"
        onClick={() => onChange(Math.max(min, qty - 1))}
        disabled={qty <= min}
        aria-label="Decrease quantity"
      >
        &minus;
      </button>
      <input
        className="sf-qty-control__input"
        type="number"
        min={min}
        value={qty}
        onChange={e => {
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= min) onChange(v);
        }}
        aria-label="Quantity"
      />
      <button
        className="sf-qty-control__btn"
        type="button"
        onClick={() => onChange(qty + 1)}
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
}

// ── ProductCard ───────────────────────────────────────────────────────────────
// Props:
//   product      — product object (productID, proFanciful, proVintage, proVarietal,
//                  proWineType, proLabelImageUrl, displayPrice, wholesaleMinQty, tierCount)
//   onDetail     — () => void, called by the "Order" button to open the side panel
//   imageShape   — 'portrait' | 'square'

export default function ProductCard({ product, onDetail, imageShape = 'portrait' }) {
  const navigate = useNavigate();

  const {
    productID,
    proBrand,
    proFanciful,
    proVintage,
    proVarietal,
    proVersion,
    proAppellation,
    proWineType,
    proLabelImageUrl,
    displayPrice,
    wholesalePrice,
    wholesaleMinQty,
    tierCount,
  } = product;

  const { primary, secondary } = formatProductDisplay({
    proBrand, proFanciful, proVintage, proVarietal, proVersion, proAppellation,
  });

  const price = displayPrice ?? wholesalePrice ?? 0;
  const labelSrc = imgSrc(proLabelImageUrl);
  const detailPath = `/wholesale/products/${productID}`;

  return (
    <div className="sf-product-card h-100">

      {/* ── Card body — navigates to product detail page ── */}
      <div
        className="sf-product-card__detail-trigger"
        onClick={() => navigate(detailPath)}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && navigate(detailPath)}
        aria-label={`View details for ${primary}`}
      >
        {/* Image area */}
        <div
          className={`sf-product-card__img-wrap ${wineTypeBgClass(proWineType)}`}
          style={imageShape === 'square' ? { aspectRatio: '1 / 1', padding: '0', overflow: 'hidden', alignItems: 'stretch' } : undefined}
        >
          {labelSrc ? (
            <img
              className="sf-product-card__img"
              src={labelSrc}
              alt={primary}
              loading="lazy"
              style={imageShape === 'square' ? {
                width: '100%',
                height: '100%',
                maxHeight: 'none',
                maxWidth: 'none',
                objectFit: 'cover',
                filter: 'none',
              } : undefined}
            />
          ) : (
            <Wine size={48} className="sf-product-card__img--placeholder" />
          )}
        </div>

        {/* Body */}
        <div className="sf-product-card__body">
          <p className="sf-product-card__name">{primary}</p>
          {secondary && <p className="sf-product-card__meta">{secondary}</p>}
          <p className="sf-product-card__price">
            <span className="sf-product-card__price-label">per case</span>
            ${Number(price).toFixed(2)}
          </p>
          {tierCount > 0 && (
            <span className="sf-product-card__tier-badge">Volume pricing available</span>
          )}
        </div>
      </div>

      {/* ── Action buttons — Details (navigate) + Order (open side panel) ── */}
      <div
        className="sf-product-card__actions"
        onClick={e => e.stopPropagation()}
      >
        <div className="d-flex gap-2 w-100">
          <Link
            to={detailPath}
            className="btn btn-outline-secondary btn-sm flex-grow-1"
          >
            Details
          </Link>
          <button
            className="btn btn-primary btn-sm flex-grow-1"
            onClick={() => onDetail && onDetail()}
          >
            Order
          </button>
        </div>
      </div>

    </div>
  );
}
